import { z } from 'zod';
/**
 * Model layout schemas — in-platform-model-view Phase 1 Session 1 (2026-04-25).
 *
 * Captures the source workbook's per-cell topology, styling, merge regions,
 * freeze panes, and section headers as a JSON artifact (`layout.json`) per
 * `(modelId, modelVersion)`. The artifact is uploaded to GCS at ingest time
 * and served to the web Model tab via `GET /api/conversations/:id/model-view`
 * (Phase 2). The grid renders cell values from the live HyperFormula engine
 * keyed by these layout coordinates.
 *
 * Naming + structure conventions:
 *  - Row + col indices are 1-based to match Excel addressing (A1 = row 1, col 1).
 *  - Only NON-empty cells are persisted in `cells[]` — empties are inferred at
 *    render time from `(row, col)` ∉ cell-set, keeping the artifact compact.
 *  - `cellType` mirrors the existing `CellClassification` taxonomy used by
 *    `packages/agents/src/lib/ingestion/cell-classifier.ts`. Adding `'empty'`
 *    here lets the per-cell parse-failure fallback (spec §1) emit a placeholder
 *    cell without crashing the rest of the layout extraction.
 *  - `tier` (Phase 2) and the Phase-3 `references` field are render-layer
 *    ONLY — they are NOT mirrored from `CellClassification` (unlike
 *    `cellType`). They are additive `.optional()` fields stamped at ingest;
 *    `undefined` on layouts produced before model-view-lovable-density-
 *    alignment (graceful-degradation back-compat, same as `text?`). Do NOT
 *    treat them as classifier-mirror-seam fields.
 *  - `composition` (Phase 4, model-view-lovable-density-alignment Inc-4b) is
 *    likewise render-layer ONLY, orthogonal to `cellType`. Stamped at ingest
 *    by the deterministic `deriveComposition` heuristic in
 *    `agents/ingestion/layout-extractor.ts` (5-guard geometry classifier;
 *    ADR 0014; spec: research/_fidelity/composition-spike.md). Additive
 *    `.optional()` — undefined on pre-feature layouts; renderer falls through
 *    to the existing G3 grid path (zero regression on un-re-ingested
 *    conversations). Grammar values: 'panel-dashboard' (DealSummary-style
 *    multi-column panels), 'columnar-brief' (DevBudget/AcqBudget-style single
 *    bordered grid), 'grid' (cash-flow / amortization fallback).
 *  - `mergeSpan` lives ONLY on the merge anchor (top-left of the merged region);
 *    every other cell in the region carries `mergedInto` pointing at the anchor.
 *    The renderer skips non-anchor cells; the anchor spans `mergeSpan.rows ×
 *    mergeSpan.cols` in the grid.
 *  - Freeze panes default to `{ frozenRows: 1, frozenCols: 1 }` when the source
 *    workbook has no `sheet.views[0]` configured — conservative, never hides a
 *    data row.
 */
// ---------------------------------------------------------------------------
// Per-cell visual style (subset of ExcelJS cell.font / cell.alignment / etc.)
// ---------------------------------------------------------------------------
export const gridCellStyleSchema = z.object({
    bold: z.boolean().optional(),
    italic: z.boolean().optional(),
    /**
     * Cell font underline. Excel uses underline (NOT bold) for column-header
     * rows in panel-dashboard sheets — e.g. DealSummary `$ Amount` / `$ / SF`
     * at row 7 of the Capital Stack Summary panel. Added 2026-05-26 so the
     * renderer can apply `text-decoration: underline` and treat these as
     * styled headers vs plain label cells.
     */
    underline: z.boolean().optional(),
    /** Excel format string (e.g. `"#,##0.00"`, `"$#,##0"`, `"0.00%"`). */
    numberFormat: z.string().optional(),
    horizontalAlign: z.enum(['left', 'center', 'right']).optional(),
    /** 6-char hex (no leading `#`). The renderer normalizes to a design token. */
    fillColor: z.string().optional(),
    borderTop: z.boolean().optional(),
    borderBottom: z.boolean().optional(),
    borderLeft: z.boolean().optional(),
    borderRight: z.boolean().optional(),
});
// ---------------------------------------------------------------------------
// Per-cell layout entry
// ---------------------------------------------------------------------------
export const gridCellSchema = z.object({
    /** 1-based row index (matches Excel addressing). */
    row: z.number().int().positive(),
    /** 1-based column index (A=1, B=2, ...). */
    col: z.number().int().positive(),
    cellType: z.enum(['label', 'input', 'formula', 'output', 'empty']),
    /** Named-range identifier (when this cell is a registered named range). */
    namedRange: z.string().optional(),
    /** Raw formula text (only for formula/output cells). */
    formula: z.string().optional(),
    /** Human-readable formula description (e.g. `"NOI = EGI − Total OpEx"`). */
    formulaDescription: z.string().optional(),
    /**
     * Raw cell text. Populated for `cellType: 'label'` cells via the
     * ingestion-side `readCellText` helper (Date / number / Date.toString
     * filtered — same filter as the section-header label-text capture).
     * Used by the renderer to display label text directly — labels do not
     * flow through the engine, so without this field they render blank.
     * Optional + back-compat: layouts produced before model-view-fidelity
     * Phase 4 (2026-04) parse with `text: undefined` and the renderer falls
     * through to its prior empty-state path.
     */
    text: z.string().optional(),
    /**
     * Render-layer cell hierarchy tier (model-view-lovable-density-alignment
     * Phase 2). ORTHOGONAL to `cellType` — NOT a classifier-mirrored field
     * (see module JSDoc). Stamped at ingest by the deterministic tier
     * classifier in `extractGridCell` from Excel fill + named-range
     * conventions (ADR 0004). Optional + back-compat (mirrors `text?`):
     * layouts / frozen_state captured before this feature parse with
     * `tier: undefined` and the renderer falls through to untiered — no
     * crash, no error (spec acceptance "Pre-existing shares degrade
     * gracefully"; ADR 0001/0002).
     */
    tier: z
        .enum([
        'super-section', // F10 NEW (model-view-excel-parity Phase 1, ADR-0004) — hierarchically ABOVE section-opener
        'section-opener',
        'description-header',
        'subtotal',
        'total',
        'grand-total',
    ])
        .optional(),
    /**
     * Phase 3 (model-view-lovable-density-alignment) — render-layer ONLY,
     * orthogonal to `cellType` (NOT a classifier-mirror field; see header
     * JSDoc :18-26). 'cross-sheet' iff the cell's formula references another
     * sheet; 'local' otherwise. Set only for formula/output cells at ingest
     * (input/label/empty cells have no formula → field omitted). Additive
     * `.optional()` — undefined on pre-feature layouts (graceful-degradation
     * back-compat, same as `text?`/`tier?`).
     */
    references: z.enum(['local', 'cross-sheet']).optional(),
    /** Set ONLY on the merge anchor (top-left). Spans `rows × cols`. */
    mergeSpan: z
        .object({
        rows: z.number().int().positive(),
        cols: z.number().int().positive(),
    })
        .optional(),
    /** Set on every cell of the merge region EXCEPT the anchor. Points back at the anchor. */
    mergedInto: z
        .object({
        row: z.number().int().positive(),
        col: z.number().int().positive(),
    })
        .optional(),
    style: gridCellStyleSchema.optional(),
});
// ---------------------------------------------------------------------------
// Composition model (model-view-lovable-density-alignment Inc-4b, 2026-05-19)
// ADR 0014: ingest-stamped, render-agnostic, additive .optional() — same
// orthogonal-field discipline as tier? and references?. See:
//   research/_fidelity/remediation-plan.md §1
//   research/_fidelity/composition-spike.md §5 (final 5-guard heuristic)
// ---------------------------------------------------------------------------
// One panel entry within a panel-dashboard layout. Each panel is independently
// bordered and represents one horizontal column-cluster in the workbook.
export const compositionPanelSchema = z.object({
    /** Human-readable panel title (the label-cell text at the cluster top). */
    label: z.string(),
    /** Inclusive start row of this panel's content (1-based). */
    startRow: z.number().int().nonnegative(),
    /** Inclusive end row of this panel's content (1-based). */
    endRow: z.number().int().nonnegative(),
    /** Leftmost column of this panel (1-based, inclusive). */
    startCol: z.number().int().nonnegative(),
    /** Rightmost column of this panel (1-based, inclusive). */
    endCol: z.number().int().nonnegative(),
    /** 0-based dashboard grid column index (cluster index from left). */
    column: z.number().int().nonnegative(),
});
// One section band within a columnar-brief layout. Superset of the legacy
// sectionHeaders[] entries (same endRow fallback chain, ADR 0012).
export const compositionSectionSchema = z.object({
    /** Human-readable section label (the lone label-cell text). */
    label: z.string(),
    /** Inclusive start row of this section (1-based). */
    startRow: z.number().int().nonnegative(),
    /** Inclusive end row of this section (1-based). */
    endRow: z.number().int().nonnegative(),
});
/**
 * Per-sheet composition model. Discriminated by `grammar`:
 *
 * - 'panel-dashboard': N-column grid of independently bordered key-value
 *   panels (DealSummary pattern). `panels[]` carries one entry per detected
 *   column-cluster. `sections` is undefined.
 * - 'columnar-brief': single full-width bordered grid with section bands +
 *   3-tier subtotal/total treatment (DevBudget/AcqBudget pattern). `sections[]`
 *   carries the band definitions. `panels` is undefined.
 * - 'grid': fallback for wide/dense/structureless sheets (cash-flow,
 *   amortization). Neither `panels` nor `sections` is populated.
 *
 * `contentColStart`/`contentColEnd` are always set (derived from the populated
 * bounding box of the sheet's non-empty cells).
 *
 * Additive `.optional()` — pre-feature layouts parse with `composition:
 * undefined`; the renderer falls through to the G3 grid (today's behavior).
 * Derivation algorithm: `deriveComposition()` in layout-extractor.ts.
 * Spike accuracy: 14/14 (100%) across both reference workbooks at K=2.
 */
export const sheetCompositionSchema = z.object({
    grammar: z.enum(['panel-dashboard', 'columnar-brief', 'grid']),
    /** Present iff grammar === 'panel-dashboard'. */
    panels: z.array(compositionPanelSchema).optional(),
    /** Present iff grammar === 'columnar-brief'. */
    sections: z.array(compositionSectionSchema).optional(),
    /** Leftmost populated column across all rows (1-based, inclusive). */
    contentColStart: z.number().int().nonnegative(),
    /** Rightmost populated column across all rows (1-based, inclusive). */
    contentColEnd: z.number().int().nonnegative(),
});
// ---------------------------------------------------------------------------
// Section header (auto-detected at extraction)
// ---------------------------------------------------------------------------
export const sheetSectionHeaderSchema = z.object({
    row: z.number().int().positive(),
    col: z.number().int().positive(),
    label: z.string(),
    /** Inclusive end row of the section. Omitted when the section runs to the bottom. */
    endRow: z.number().int().positive().optional(),
});
// ---------------------------------------------------------------------------
// Per-sheet layout
// ---------------------------------------------------------------------------
export const sheetLayoutSchema = z.object({
    name: z.string(),
    index: z.number().int().nonnegative(),
    rowCount: z.number().int().nonnegative(),
    colCount: z.number().int().nonnegative(),
    /** Row index (as string key) → height in px. Only set when non-default. */
    rowHeights: z.record(z.string(), z.number()).optional(),
    /** Column letter → width in px. Only set when non-default. */
    colWidths: z.record(z.string(), z.number()).optional(),
    /** From ExcelJS `sheet.views[0].ySplit`; fallback 1 (never hides a data row). */
    frozenRows: z.number().int().nonnegative().default(1),
    /** From ExcelJS `sheet.views[0].xSplit`; fallback 1 (never hides a data column). */
    frozenCols: z.number().int().nonnegative().default(1),
    /** Only non-empty cells. Empties inferred at render time. */
    cells: z.array(gridCellSchema),
    /** Detected section labels (e.g., "Capital Stack"). May be empty. */
    sectionHeaders: z.array(sheetSectionHeaderSchema).default([]),
    /**
     * Composition model (model-view-lovable-density-alignment Inc-4b,
     * 2026-05-19). Additive .optional() — undefined on pre-feature layouts;
     * renderer falls through to G3 grid (zero regression). See
     * sheetCompositionSchema JSDoc for grammar values + derivation algorithm.
     * ADR 0014 precedent: same orthogonal-ingest-stamp discipline as tier? /
     * references?.
     */
    composition: sheetCompositionSchema.optional(),
});
// ---------------------------------------------------------------------------
// Top-level layout artifact (one per (modelId, modelVersion))
// ---------------------------------------------------------------------------
export const modelLayoutSchema = z.object({
    modelId: z.string(),
    modelVersion: z.string(),
    /** Visible sheets in workbook order. Supplementary sheets excluded at extraction. */
    sheets: z.array(sheetLayoutSchema),
    /**
     * Drives the grid's bottom tab bar — workbook-ordered, with metadata sheets
     * (`UX Behavior Notes`, `DevNotes_*`, etc.) excluded per `isSupplementarySheet`.
     * Always equals `sheets.map(s => s.name)` post-extraction; surfaced separately
     * so the frontend can render the tab bar without iterating `sheets[]`.
     */
    visibleSheetNames: z.array(z.string()),
});
